// Shared helper for the "don't swallow exceptions" pattern (W4-2).
// Logs to console with a tag so silent failures are diagnosable, and
// optionally shows a user-facing toast when a toast function is provided.
//
// Usage:
//   try { ... } catch (err) { logError('comms.loadGuests', err) }
//   try { ... } catch (err) { logError('profile.save', err, { toast: show, t }) }

import { Sentry } from '../lib/sentry.js'

export function logError(tag, err, opts = {}) {
  const msg = err && err.message ? err.message : String(err)
  // eslint-disable-next-line no-console
  console.error(`[${tag}]`, err)
  // W8: forward handled errors to Sentry (warning level + `handled` tag) so they
  // are observable in production, not only in the user's console. The Sentry
  // export is a buffering no-op proxy until the SDK loads and never throws; the
  // tags make these trivial to sample/filter apart from unhandled crashes.
  try {
    Sentry.captureException(err instanceof Error ? err : new Error(msg), {
      level: 'warning',
      tags: { handled: true, logTag: String(tag) },
    })
  } catch { /* reporting must never break the callsite */ }
  if (opts.toast && typeof opts.toast === 'function') {
    const fallback = opts.toastMessage
      || (opts.t && opts.t('operationFailed'))
      || 'Something went wrong'
    try { opts.toast(fallback, 'error') } catch { /* toast itself should never break the callsite */ }
  }
  return msg
}

export default logError
